// material ui
import { Grid, Typography } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import React from 'react';

// components
import { MultiAccordionPanel } from '~/src/dash/components/accordion/multi-accordion-panel';
import { IInputRow, ITabRow } from '~/src/dash/components/input-row';

// COMPONENT DESCRIPTION: Multi Accordion handles the expansion / contraction of
// all the child panels.  The panel is a section that can expand and contract on its
// own and the rows are the children of this panel that appear when expanded.
export interface IAccordionPanel {
  // display title for the panel
  title: string;
  // row information, more info in IInputRow definition
  rows: IInputRow[];
  // panel chip title (i.e. Active OR Custom)
  panelActiveChipTitle: string;
  // panel inactive row tag which is the default grayed out title (i.e. Inactive, None, All Channels )
  // this is shown when no active tags are given by the individual rows
  panelInactiveRowTag: string;
  // panel-specific error text
  optionalError?: string;
  // disable active tags for individual rows
  disableActiveTags?: boolean;
  // enables tabs to display different sets of rows in the panel
  enableTabs?: boolean;
  // if enableTabs, tab rows must be in props
  tabRows?: ITabRow[];
  // endpoint request scorecard
  endpointScorecard?: boolean;
  percentCompleted?: number;
  paramsLeft?: number;
  percentLeft?: number;
}

interface IProps {
  // title of the parent accordion
  title: string;
  subtitle?: string;
  subtitle2?: string;
  // information for the accordion panels
  panels: IAccordionPanel[];
  // disableEdit
  disableEdit: boolean;
  // defaults the panels being open
  forcePanelsOpen?: boolean;
  // styling for endpoint scorecard
  endpointScorecard?: boolean;
}

interface IState {
  // holds title of expanded panels
  expandedPanels: string[];
}

export class MultiAccordion extends React.Component<IProps, IState> {
  state = {
    expandedPanels: [] as string[],
  };

  componentDidMount = () => {
    const expandedPanels = [];
    // iterate over panels and determine if any of the rows are not default values (active)
    // and expand them on initial load
    for (const panel of this.props.panels) {
      if (this.props.forcePanelsOpen) {
        expandedPanels.push(panel.title);
        continue;
      }
      let expanded = false;
      if (panel.enableTabs && panel.tabRows) {
        for (const tabRow of panel.tabRows) {
          for (const row of tabRow.rows) {
            if (row.isActiveValue1() && !expanded) {
              expandedPanels.push(panel.title);
              expanded = true;
            }
          }
        }
      } else {
        for (const row of panel.rows) {
          if (row.isActiveValue1() && !expanded) {
            expandedPanels.push(panel.title);
            expanded = true;
          }
        }
      }
    }
    this.setState({
      expandedPanels,
    });
  };

  // -------- COMPONENT HELPER FUNCTIONS -------- //

  // checks if all panels are currently expanded
  allPanelsExpanded = () => {
    return this.props.panels.length === this.state.expandedPanels.length;
  };

  // if not all panels are expanded, expand them all
  // if all panels are expanded, collapse them all
  expandAllPanels = () => {
    const expandedPanels = [];
    if (!this.allPanelsExpanded()) {
      for (const panel of this.props.panels) {
        expandedPanels.push(panel.title);
      }
    }
    this.setState({
      expandedPanels,
    });
  };

  // -------- HANDLER HELPER FUNCTIONS -------- //

  // expands or contracts a single panel
  handleExpandPanel = (panelTitle: string) => {
    let expandedPanels = [...this.state.expandedPanels];
    if (expandedPanels.includes(panelTitle)) {
      expandedPanels = expandedPanels.filter((title) => title !== panelTitle);
    } else {
      expandedPanels.push(panelTitle);
    }
    this.setState({
      expandedPanels,
    });
  };

  render() {
    const { title, panels, disableEdit, subtitle, subtitle2 } = this.props;
    return (
      <Grid item>
        {/* Accordion Header Row */}
        <Grid
          container
          style={
            this.props.endpointScorecard
              ? {
                  border: '1px solid rgba(0, 0, 0, .125)',
                  backgroundColor: '#fff',
                  padding: 12,
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                }
              : {
                  background: '#F5F5F5 0% 0% no-repeat padding-box',
                  borderRadius: '3px 3px 0px 0px',
                  opacity: 1,
                  padding: '12px 12px',
                }
          }
          justify='space-between'
          alignItems='center'
        >
          <Grid item style={{ display: 'flex', flexDirection: 'column' }}>
            <Grid item>
              <Typography
                style={
                  this.props.endpointScorecard
                    ? {
                        fontSize: 18,
                        fontWeight: 500,
                      }
                    : {
                        color: '#707070',
                        fontSize: 14,
                        fontWeight: 500,
                      }
                }
              >
                {title}
              </Typography>
            </Grid>
            {!!subtitle && (
              <Grid item>
                <Typography
                  style={{
                    color: '#707070',
                    fontSize: this.props.endpointScorecard ? 14 : 12,
                    fontWeight: 400,
                  }}
                >
                  {subtitle}
                </Typography>
              </Grid>
            )}
            {!!subtitle2 && (
              <Grid item>
                <Typography
                  style={{
                    color: '#707070',
                    fontSize: this.props.endpointScorecard ? 14 : 12,
                    fontWeight: 400,
                  }}
                >
                  {subtitle2}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid item style={{ marginRight: 5, cursor: 'pointer' }} onClick={this.expandAllPanels}>
            {this.allPanelsExpanded() ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Grid>
        </Grid>

        {/* Accordion Panels */}
        {panels.map((panel: IAccordionPanel) => {
          return (
            <MultiAccordionPanel
              key={`multi-accordion-panel-${panel.title}`}
              panel={panel}
              isExpanded={this.state.expandedPanels.includes(panel.title)}
              handleExpandPanel={this.handleExpandPanel}
              disableEdit={disableEdit}
              disableActiveTags={panel.disableActiveTags}
            />
          );
        })}
      </Grid>
    );
  }
}
